import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { Visibility } from '../api/types';
import { PlaylistCard } from '../components/cards';
import { IconLayers, IconPlus } from '../components/icons';
import { CardSkeletons, EmptyState, ErrorState, Field, Segmented, TextArea, TextInput } from '../components/ui';
import { useAsync } from '../hooks/useAsync';
import { messageOf, visibilityOptions } from '../lib/format';
import { t } from '../i18n';

export function PlaylistsPage() {
  const state = useAsync(() => api.playlists.mine(), []);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t('nav.playlists')}</h1>
          <p className="page-head__sub">{t('playlists.subtitle')}</p>
        </div>
        <Link className="btn btn--primary" to="/app/playlists/new">
          <IconPlus size={18} /> {t('playlists.new')}
        </Link>
      </div>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading ? (
        <CardSkeletons count={3} variant="playlist" />
      ) : state.data && state.data.length > 0 ? (
        <div className="playlist-grid">
          {state.data.map((playlist) => (
            <PlaylistCard key={playlist.id} playlist={playlist} to={`/app/playlists/${playlist.id}`} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<IconLayers />}
          title={t('playlists.emptyTitle')}
          body={t('playlists.emptyBody')}
          action={
            <Link className="btn btn--primary" to="/app/playlists/new">
              <IconPlus size={18} /> {t('playlists.createFirst')}
            </Link>
          }
        />
      )}
    </div>
  );
}

export function NewPlaylistPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('private');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) {
      setError(t('playlists.titleRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.playlists.create({ title: title.trim(), description, visibility });
      navigate(`/app/playlists/${created.id}/edit`, { replace: true });
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  const visOptions = visibilityOptions();

  return (
    <div className="page page--read">
      <p className="tiny" style={{ marginBottom: 'var(--sp-4)' }}>
        <Link to="/app/playlists">{t('playlists.backAll')}</Link>
      </p>
      <h1 style={{ fontSize: 'var(--fs-24)', marginBottom: 'var(--sp-5)' }}>{t('playlists.new')}</h1>
      <form className="panel stack" onSubmit={onSubmit}>
        <Field label={t('playlists.fieldTitle')} htmlFor="pl-title" error={error ?? undefined}>
          <TextInput
            id="pl-title"
            value={title}
            invalid={Boolean(error)}
            placeholder={t('playlists.titlePlaceholder')}
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label={t('playlists.fieldDescription')} htmlFor="pl-desc" hint={t('playlists.descriptionHint')}>
          <TextArea
            id="pl-desc"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Segmented
          name="visibility"
          label={t('playlists.whoSees')}
          value={visibility}
          onChange={setVisibility}
          options={visOptions.map((option) => ({ value: option.value, label: option.label }))}
        />
        <p className="field__hint">{visOptions.find((option) => option.value === visibility)?.hint}</p>
        <div className="row">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? t('auth.creating') : t('playlists.createAndAdd')}
          </button>
          <Link className="btn btn--ghost" to="/app/playlists">
            {t('ui.cancel')}
          </Link>
        </div>
      </form>
    </div>
  );
}
